import React, { useEffect, useState } from "react";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { useReadOnlyMode } from "../../hooks/useReadOnlyMode";

interface GitHubStatus {
  connected: boolean;
  github_username?: string;
  github_email?: string;
  scopes: string[];
  connected_at?: string;
  avatar_url?: string;
  manage_url?: string;
}

const GitHubConnection: React.FC = () => {
  const { isReadOnly } = useReadOnlyMode();
  const api = useAuthenticatedApi();
  const [status, setStatus] = useState<GitHubStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = async () => {
    try {
      const res = await api.get("/github/status");
      setStatus(res);
    } catch (e) {
      console.error("Failed to fetch GitHub status", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const connect = async () => {
    if (isReadOnly) return;
    setConnecting(true);
    try {
      const res = await api.get("/github/connect");
      window.location.href = res.authorization_url;
    } catch (e) {
      console.error("Failed to initiate GitHub connection", e);
      setConnecting(false);
    }
  };

  const confirmDisconnect = async () => {
    if (isReadOnly) return;
    setDisconnecting(true);
    try {
      await api.delete("/github/disconnect");
      await refresh();
      setShowDisconnectModal(false);
    } catch (e) {
      console.error("Failed to disconnect GitHub", e);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-slate-200 dark:bg-neutral-700 rounded w-3/4 mb-3"></div>
        <div className="h-10 bg-slate-200 dark:bg-neutral-700 rounded w-32"></div>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-neutral-600 dark:text-neutral-400">
          Connect your GitHub account to import repositories, commit changes,
          and create pull requests.
        </div>
        <button
          onClick={connect}
          disabled={connecting || isReadOnly}
          className={`px-4 py-2 rounded-lg transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${
            isReadOnly
              ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
              : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
          }`}
          title={isReadOnly ? 'Upgrade to connect GitHub' : ''}
        >
          {connecting ? "Connecting…" : "Connect GitHub"}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-slate-100 dark:bg-neutral-800 border border-slate-300 dark:border-neutral-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {status.avatar_url && (
              <img
                src={status.avatar_url}
                alt="avatar"
                className="w-10 h-10 rounded-full border-2 border-slate-300 dark:border-neutral-600"
              />
            )}
            <div>
              <div className="font-medium text-neutral-900 dark:text-white">
                @{status.github_username}
              </div>
              {status.github_email && (
                <div className="text-xs text-neutral-600 dark:text-neutral-400">
                  {status.github_email}
                </div>
              )}
              <div className="text-xs text-neutral-600 dark:text-neutral-400">
                Scopes: {(status.scopes || []).join(", ") || "none"}
              </div>
            </div>
          </div>
          {status.manage_url ? (
            <a
              href={status.manage_url}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-lg bg-slate-200 dark:bg-neutral-700 text-neutral-900 dark:text-white hover:bg-slate-300 dark:hover:bg-neutral-600 transition-colors text-sm font-medium"
            >
              Manage on GitHub
            </a>
          ) : (
            <button
              onClick={() => setShowDisconnectModal(true)}
              disabled={isReadOnly}
              className={`px-3 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed ${
                isReadOnly
                  ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
              }`}
              title={isReadOnly ? 'Upgrade to disconnect GitHub' : ''}
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      {showDisconnectModal && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !disconnecting && setShowDisconnectModal(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-slate-300 dark:border-neutral-700 overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-slate-300 dark:border-neutral-700">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                  Disconnect GitHub
                </h3>
              </div>
              <div className="px-5 py-4 text-sm text-neutral-700 dark:text-neutral-300">
                Are you sure you want to disconnect your GitHub account? You can
                reconnect at any time.
              </div>
              <div className="px-5 py-4 flex justify-end gap-2 border-t border-slate-300 dark:border-neutral-700">
                <button
                  className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-neutral-700 text-neutral-900 dark:text-white hover:bg-slate-300 dark:hover:bg-neutral-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => setShowDisconnectModal(false)}
                  disabled={disconnecting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GitHubConnection;
